import React from 'react';
import BaseScadaNode from './BaseScadaNode';

export function CompressorSVG({ config = {}, width = 80, height = 60, liveValues = {} }) {
    const running = parseFloat(liveValues.state ?? 1) >= parseFloat(config.threshold ?? 0.5);
    const color = running ? '#22C55E' : '#64748B';

    const flywheelR = Math.min(height * 0.27, 14);
    const flywheelCX = flywheelR + 6;
    const flywheelCY = height / 2;

    const cylX = flywheelCX + flywheelR + 10;
    const cylW = width - cylX - 4;
    const cylH = height * 0.36;
    const cylY = flywheelCY - cylH / 2;

    const rodLen = flywheelR * 2;
    const rodH = cylH * 0.44;
    const rodY = flywheelCY - rodH / 2;
    const rodX0 = flywheelCX + flywheelR - 2;

    const pressureBound = liveValues.pressure != null;
    const pressureVal = parseFloat(liveValues.pressure ?? 0);

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {/* Flywheel group — animateTransform only rotates this group */}
            <g>
                <circle cx={flywheelCX} cy={flywheelCY} r={flywheelR}
                    fill="#1E293B" stroke={color} strokeWidth={2} />
                {[0, 120, 240].map(deg => {
                    const rad = deg * Math.PI / 180;
                    return (
                        <line key={deg}
                            x1={flywheelCX} y1={flywheelCY}
                            x2={flywheelCX + Math.cos(rad) * flywheelR * 0.8}
                            y2={flywheelCY + Math.sin(rad) * flywheelR * 0.8}
                            stroke={color} strokeWidth={2} strokeLinecap="round" />
                    );
                })}
                <circle cx={flywheelCX} cy={flywheelCY} r={3} fill={color} />
                {running && (
                    <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from={`0 ${flywheelCX} ${flywheelCY}`}
                        to={`360 ${flywheelCX} ${flywheelCY}`}
                        dur="0.8s"
                        repeatCount="indefinite"
                    />
                )}
            </g>

            {/* Connecting rod — outside flywheel group so it doesn't rotate */}
            <rect y={rodY} width={rodLen} height={rodH} rx={2}
                fill="#334155" stroke={color} strokeWidth={1}
                x={rodX0}>
                {running && (
                    <animate
                        attributeName="x"
                        values={`${rodX0};${rodX0 + 7};${rodX0}`}
                        dur="0.8s"
                        repeatCount="indefinite"
                    />
                )}
            </rect>

            {/* Cylinder body */}
            <rect x={cylX} y={cylY} width={cylW} height={cylH}
                rx={3} fill="#0F172A" stroke={color} strokeWidth={2} />

            {/* Piston inside cylinder */}
            <rect y={cylY + 3} width={8} height={cylH - 6} rx={1}
                fill={color} opacity={0.6}
                x={cylX + 4}>
                {running && (
                    <animate
                        attributeName="x"
                        values={`${cylX + 4};${cylX + cylW - 14};${cylX + 4}`}
                        dur="0.8s"
                        repeatCount="indefinite"
                    />
                )}
            </rect>

            {/* Status dot on right side of cylinder */}
            <circle cx={cylX + cylW - 8} cy={cylY + 8} r={4} fill={color} stroke="#0F172A" strokeWidth={1} />

            {/* Pressure text below */}
            {pressureBound && (
                <text x={cylX + cylW / 2} y={height - 3}
                    textAnchor="middle" fill="#F1F5F9" fontSize={9} fontWeight="bold">
                    {isNaN(pressureVal) ? '--' : pressureVal.toFixed(1)} bar
                </text>
            )}
        </svg>
    );
}

export default function CompressorNode(props) {
    const { data } = props;
    const w = data?.width || 80;
    const h = data?.height || 60;
    return (
        <BaseScadaNode {...props}>
            <CompressorSVG config={data?.config} width={w} height={h} liveValues={data?.liveValues || {}} />
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', marginTop: 2,
                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
